import { createContext, useContext, useReducer, useEffect, useRef, useCallback, type ReactNode } from 'react'
import { doc, setDoc, onSnapshot } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { useAuth } from './AuthContext'
import { initRevenueCat, getPurchases, ENTITLEMENT_ID } from '../lib/revenuecat'
import type { SubscriptionTier, SubscriptionState } from '../types/index'

interface AuthUser {
  uid: string
  email: string | null
}

interface AuthContextValue {
  user: AuthUser | null
  loading: boolean
  login: (email: string, password: string) => Promise<unknown>
  signup: (email: string, password: string) => Promise<unknown>
  logout: () => Promise<void>
}

interface SubscriptionContextValue {
  subscription: SubscriptionState
  isPro: boolean
  loading: boolean
  purchasePackage: (packageId: string) => Promise<void>
  restorePurchases: () => Promise<void>
}

const SubscriptionContext = createContext<SubscriptionContextValue | null>(null)

const DEFAULT_STATE: SubscriptionState = {
  tier: 'free',
  isActive: false,
  loading: true,
}

type Action =
  | { type: 'LOAD'; payload: SubscriptionState }
  | { type: 'SET_TIER'; payload: { tier: SubscriptionTier; isActive: boolean } }
  | { type: 'SET_LOADING'; payload: boolean }

function getStorageKey(uid: string) {
  return `liquid-finance-subscription-${uid}`
}

function loadFromLocal(uid: string): SubscriptionState {
  try {
    const raw = localStorage.getItem(getStorageKey(uid))
    if (!raw) return { ...DEFAULT_STATE }
    return { ...DEFAULT_STATE, ...JSON.parse(raw), loading: false }
  } catch {
    return { ...DEFAULT_STATE }
  }
}

function saveToLocal(uid: string, state: SubscriptionState) {
  try {
    localStorage.setItem(getStorageKey(uid), JSON.stringify({ tier: state.tier, isActive: state.isActive }))
  } catch {
    // localStorage unavailable
  }
}

function reducer(state: SubscriptionState, action: Action): SubscriptionState {
  switch (action.type) {
    case 'LOAD':
      return action.payload
    case 'SET_TIER':
      return { ...state, tier: action.payload.tier, isActive: action.payload.isActive, loading: false }
    case 'SET_LOADING':
      return { ...state, loading: action.payload }
    default:
      return state
  }
}

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth() as AuthContextValue
  const [subscription, dispatch] = useReducer(reducer, DEFAULT_STATE)
  const firestoreLoaded = useRef(false)
  const isLocalDispatch = useRef(false)

  // Subscribe to Firestore subscription data
  useEffect(() => {
    firestoreLoaded.current = false
    isLocalDispatch.current = false

    if (!user?.uid) {
      dispatch({ type: 'LOAD', payload: { ...DEFAULT_STATE, loading: false } })
      return
    }

    // Load from localStorage for fast display
    const local = loadFromLocal(user.uid)
    dispatch({ type: 'LOAD', payload: local })

    // Init RevenueCat and check entitlements
    const purchases = initRevenueCat(user.uid)
    if (purchases) {
      purchases.getCustomerInfo().then((info) => {
        const hasProEntitlement = info.entitlements.active[ENTITLEMENT_ID] !== undefined
        if (hasProEntitlement) {
          isLocalDispatch.current = true
          dispatch({ type: 'SET_TIER', payload: { tier: 'pro', isActive: true } })
        }
      }).catch(() => {
        // RevenueCat unavailable — fall back to Firestore
      })
    }

    // Subscribe to Firestore for real-time sync
    const docRef = doc(db, 'userSubscriptions', user.uid)
    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data()
          firestoreLoaded.current = true
          isLocalDispatch.current = false
          const state: SubscriptionState = {
            tier: data.tier || 'free',
            isActive: data.isActive ?? false,
            loading: false,
            expiresAt: data.expiresAt,
            productId: data.productId,
          }
          dispatch({ type: 'LOAD', payload: state })
          saveToLocal(user.uid, state)
        } else {
          firestoreLoaded.current = true
          dispatch({ type: 'SET_LOADING', payload: false })
        }
      },
      () => {
        firestoreLoaded.current = true
        dispatch({ type: 'SET_LOADING', payload: false })
      },
    )

    return unsubscribe
  }, [user?.uid])

  // Sync local changes to Firestore
  useEffect(() => {
    if (!user?.uid || !firestoreLoaded.current || !isLocalDispatch.current) return
    isLocalDispatch.current = false
    saveToLocal(user.uid, subscription)
    const docRef = doc(db, 'userSubscriptions', user.uid)
    setDoc(docRef, { tier: subscription.tier, isActive: subscription.isActive }, { merge: true }).catch(() => {})
  }, [subscription, user?.uid])

  const purchasePackage = useCallback(async (packageId: string) => {
    const purchases = getPurchases()
    if (!purchases) {
      console.warn('RevenueCat not configured — cannot process purchase')
      return
    }

    try {
      const offerings = await purchases.getOfferings()
      const currentOffering = offerings.current
      if (!currentOffering) throw new Error('No offerings available')

      const pkg = currentOffering.availablePackages.find((p) => p.identifier === packageId)
      if (!pkg) throw new Error(`Package ${packageId} not found`)

      const { customerInfo } = await purchases.purchase({ rcPackage: pkg })
      const hasProEntitlement = customerInfo.entitlements.active[ENTITLEMENT_ID] !== undefined

      if (hasProEntitlement) {
        isLocalDispatch.current = true
        dispatch({ type: 'SET_TIER', payload: { tier: 'pro', isActive: true } })
      }
    } catch (err) {
      console.error('Purchase failed:', err)
      throw err
    }
  }, [])

  const restorePurchases = useCallback(async () => {
    const purchases = getPurchases()
    if (!purchases) return

    try {
      const info = await purchases.getCustomerInfo()
      const hasProEntitlement = info.entitlements.active[ENTITLEMENT_ID] !== undefined
      isLocalDispatch.current = true
      dispatch({
        type: 'SET_TIER',
        payload: { tier: hasProEntitlement ? 'pro' : 'free', isActive: hasProEntitlement },
      })
    } catch (err) {
      console.error('Restore failed:', err)
      throw err
    }
  }, [])

  const isPro = subscription.tier === 'pro' && subscription.isActive

  return (
    <SubscriptionContext.Provider value={{ subscription, isPro, loading: subscription.loading, purchasePackage, restorePurchases }}>
      {children}
    </SubscriptionContext.Provider>
  )
}

export function useSubscription() {
  const ctx = useContext(SubscriptionContext)
  if (!ctx) throw new Error('useSubscription must be used within SubscriptionProvider')
  return ctx
}
